import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { WINDOW } from '@trimble-gcs/ngx-common';
import {
  combineLatest,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AppState } from '../../../app-state/app.state';
import { ScandataService } from '../../../scandata/scandata.service';
import { ALLOWED_EXTENSIONS, ImportFile } from '../../import.models';
import { allowImportForFileType } from '../../import.service';
import { PickerType } from '../file-picker.models';

@Injectable({
  providedIn: 'root',
})
export class GoogleDriveFilePickerService {
  private readonly scandataService = inject(ScandataService);
  private readonly _window = inject(WINDOW);
  private readonly store = inject(Store);

  private readonly scope = 'https://www.googleapis.com/auth/drive.file';
  private readonly mimeTypes: string[] = ['application/octet-stream'];
  private readonly tokenRemainingMinutesThreshold = 45;

  private tokenClient?: google.accounts.oauth2.TokenClient;
  private oauthTokenExpires?: Date;
  private oauthToken?: string;

  private pickerApiLoaded = false;
  private picker?: google.picker.Picker;
  private selectedFiles$!: Subject<ImportFile[]>;

  private get googleDriveSettings() {
    return this.store.selectSnapshot(AppState.settings).googleDrive;
  }

  show(): Observable<ImportFile[]> {
    this.selectedFiles$ = new Subject<ImportFile[]>();

    const initializeAuth$ = from(this.initializeAuth());
    const initializePicker$ = from(this.initializePicker());

    return combineLatest([initializeAuth$, initializePicker$]).pipe(
      tap(() => this.openPicker()),
      switchMap(() => this.selectedFiles$.asObservable()),
      take(1),
      finalize(() => this.picker?.setVisible(false)),
    );
  }

  private initializeAuth() {
    return new Promise<void>((resolve, reject) => {
      if (!this.requireTokenRenew()) return resolve();

      this.tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: this.googleDriveSettings.clientId,
        scope: this.scope,
        callback: (authResult: google.accounts.oauth2.TokenResponse) => {
          if (authResult.error) return reject(authResult.error);

          // Clear the picker so it can be rebuilt with the new auth token
          this.picker = undefined;

          const expires = new Date(new Date().getTime() + parseInt(authResult.expires_in) * 1000);
          this.oauthTokenExpires = expires;
          this.oauthToken = authResult.access_token;

          resolve();
        },
      });

      this.tokenClient.requestAccessToken({ prompt: '' });
    });
  }

  private requireTokenRenew() {
    if (isNil(this.oauthToken) || isNil(this.oauthTokenExpires)) return true;

    const tokenRemainingMinutes = (this.oauthTokenExpires.getTime() - new Date().getTime()) / 60000;
    if (tokenRemainingMinutes < this.tokenRemainingMinutesThreshold) return true;

    return false;
  }

  private initializePicker() {
    return new Promise<void>((resolve, reject) => {
      if (this.pickerApiLoaded) return resolve();

      gapi.load('picker', {
        callback: () => {
          this.pickerApiLoaded = true;
          resolve();
        },
        onerror: () => reject('Failed to initialise Google Drive picker API'),
      });
    });
  }

  private openPicker() {
    if (isNil(this.oauthToken)) return;

    if (isNil(this.picker)) {
      const origin = Array.from(this._window.location.ancestorOrigins).at(-1)!;
      const view = new google.picker.DocsView(google.picker.ViewId.DOCS);
      view
        .setIncludeFolders(true)
        .setMimeTypes(this.mimeTypes.join(','))
        .setQuery(ALLOWED_EXTENSIONS.map((ext) => '(title:"' + ext + '")').join(' | '));

      const settings = this.googleDriveSettings;

      this.picker = new google.picker.PickerBuilder()
        .setOAuthToken(this.oauthToken)
        .setOrigin(origin)
        .setAppId(settings.consoleProjectId)
        .setDeveloperKey(settings.key)
        .addView(view)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setCallback((result: google.picker.ResponseObject) => this.pickerCallback(result))
        .build();
    }

    this.picker.setVisible(true);
  }

  private pickerCallback(result: google.picker.ResponseObject) {
    if (result.action === google.picker.Action.CANCEL) {
      this.selectedFiles$.complete();
      return;
    }

    if (result.action === google.picker.Action.PICKED) {
      if (isNil(result.docs)) return this.selectedFiles$.complete();

      const files$ =
        result.docs
          .filter(
            (doc) =>
              isDefined(doc.name) && isDefined(doc.sizeBytes) && allowImportForFileType(doc.name),
          )
          .map((doc) => this.mapToImportFile(doc)) ?? [];

      forkJoin(files$).subscribe((files) => {
        this.selectedFiles$.next(files);
      });
    }
  }

  private mapToImportFile(doc: google.picker.DocumentObject): Observable<ImportFile> {
    const externalFileId = isDefined(doc.lastEditedUtc)
      ? `googleDrive-${doc.id}-${doc.lastEditedUtc}`
      : `googleDrive-${doc.id}`;

    const alreadyIngested$ = this.scandataService
      .getScansByQuery({ filters: { externalFileId } })
      .pipe(map((scans) => scans.length > 0));

    return alreadyIngested$.pipe(
      map((alreadyIngested) => ({
        id: doc.id,
        name: doc.name!,
        size: doc.sizeBytes!,
        pickerType: PickerType.GoogleDrive,
        downloadUrl: `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`,
        downloadToken: this.oauthToken,
        externalFileId,
        alreadyIngested,
      })),
    );
  }
}
